package com.arenaproplay.admin.ui.layouts.shell

import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.navigation.Routes
import com.arenaproplay.admin.state.auth.LocalAuthSession
import com.arenaproplay.admin.state.shell.ShellCounts
import com.arenaproplay.admin.ui.components.icons.LucideIcon
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.utils.sBorderRadius
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*

/**
 * Mobile slide-in navigation drawer (kontaq pattern).
 *
 * Shown only when [open] is true.  Contains the full nav from NavDefs
 * with icon + label rows, active state, badges, Настройки pinned at
 * bottom, and logout.  Tapping an item navigates and closes the drawer.
 *
 * The drawer slides in from the LEFT (opposite to the right-side content
 * Drawer used for detail panels).  It uses the same arena-drawer-left
 * keyframe family injected by [MobileNavDrawerStyles].
 *
 * z-index: 700 — above content drawers (500), below command palette (999).
 */
@Composable
fun MobileNavDrawer(
    open: Boolean,
    onClose: () -> Unit,
    counts: ShellCounts = ShellCounts(),
) {
    MobileNavDrawerStyles()

    // Slide-out: держим панель смонтированной 200мс после закрытия (паттерн Drawer.kt)
    var visible by remember { mutableStateOf(open) }
    var closing by remember { mutableStateOf(false) }
    LaunchedEffect(open) {
        if (open) { closing = false; visible = true } else {
            closing = true
            kotlinx.coroutines.delay(200)
            visible = false; closing = false
        }
    }
    if (!visible) return

    val ctx = rememberPageContext()
    val session = LocalAuthSession.current
    val currentRoute = ctx.route.path

    // Body scroll lock while open.
    DisposableEffect(Unit) {
        lockBodyScroll()
        onDispose { unlockBodyScroll() }
    }

    // Keyboard: Esc closes.
    DisposableEffect(Unit) {
        val handler: (dynamic) -> Unit = { event ->
            if ((event.key as? String) == "Escape") onClose()
        }
        document.addEventListener("keydown", handler)
        onDispose { document.removeEventListener("keydown", handler) }
    }

    // Swipe-left to dismiss (touch handling).
    var touchStartX by remember { mutableStateOf(0.0) }
    var touchStartY by remember { mutableStateOf(0.0) }

    // Scrim (backdrop)
    Box(
        Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(699)
            .styleModifier {
                property("background", "rgba(0,0,0,0.60)")
                property("backdrop-filter", "blur(2px)")
                property("-webkit-backdrop-filter", "blur(2px)")
                property("animation", if (closing) "na-scrim-out 200ms ease both" else "na-scrim-in 200ms ease both")
            }
            .onClick { onClose() }
    )

    // Slide-in panel from left
    Column(
        Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).bottom(0.px)
            .width(280.px)
            .maxWidth(86.percent)
            .zIndex(700)
            .styleModifier {
                property("overflow", "hidden")
                property("background", "#070C16")
                property("border-right", "1px solid rgba(255,255,255,.08)")
                property("box-shadow", "8px 0 40px rgba(0,0,0,0.55)")
                property("animation", if (closing) "na-drawer-left-out 200ms cubic-bezier(0.16, 1, 0.3, 1) both" else "na-drawer-left-in 220ms cubic-bezier(0.16, 1, 0.3, 1) both")
            }
            .attrsModifier {
                onTouchStart { e ->
                    val t = e.nativeEvent.asDynamic()
                    if (t.touches != null && t.touches.length as Int > 0) {
                        touchStartX = t.touches[0].clientX.unsafeCast<Double>()
                        touchStartY = t.touches[0].clientY.unsafeCast<Double>()
                    }
                }
                onTouchEnd { e ->
                    val t = e.nativeEvent.asDynamic()
                    val endX = if (t.changedTouches != null && t.changedTouches.length as Int > 0)
                        t.changedTouches[0].clientX.unsafeCast<Double>() else touchStartX
                    val endY = if (t.changedTouches != null && t.changedTouches.length as Int > 0)
                        t.changedTouches[0].clientY.unsafeCast<Double>() else touchStartY
                    val dx = endX - touchStartX
                    val dy = endY - touchStartY
                    // Swipe left (dx < -60) with horizontal intent (|dx| > |dy|)
                    if (dx < -60 && kotlin.math.abs(dx) > kotlin.math.abs(dy)) {
                        onClose()
                    }
                }
            }
    ) {
        // ── Header ─────────────────────────────────────────────────────────────
        Row(
            Modifier
                .fillMaxWidth()
                .padding(leftRight = 16.px, topBottom = 18.px)
                .styleModifier { property("border-bottom", "1px solid rgba(255,255,255,.06)") },
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Logo chip
            Box(
                Modifier
                    .width(32.px).height(32.px)
                    .sBorderRadius(10)
                    .styleModifier {
                        property("background", "linear-gradient(135deg,#0061FF,#4D8CFF)")
                        property("flex-shrink", "0")
                    },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    "A",
                    Modifier.styleModifier {
                        property("font-family", "'Unbounded', sans-serif")
                        property("font-weight", "700")
                        property("font-size", "13px")
                        property("color", "#FFFFFF")
                        property("line-height", "1")
                    }
                )
            }
            Box(Modifier.width(10.px))
            SpanText(
                "Arena Admin",
                Modifier.styleModifier {
                    property("font-family", "'Unbounded', sans-serif")
                    property("font-size", "13px")
                    property("font-weight", "600")
                    property("color", "#F0F4FC")
                    property("letter-spacing", "-0.02em")
                }
            )
        }

        // ── Nav items ─────────────────────────────────────────────────────────
        Column(
            Modifier
                .fillMaxWidth()
                .weight(1f)
                .styleModifier {
                    property("overflow-y", "auto")
                    property("overflow-x", "hidden")
                    property("scrollbar-width", "none")
                    property("-ms-overflow-style", "none")
                }
                .padding(topBottom = 10.px, leftRight = 10.px)
        ) {
            appNavGroups.forEach { group ->
                group.items.forEach { item ->
                    val isActive = isDrawerActiveRoute(currentRoute, item)
                    val badgeCount: Int? = when {
                        item.route == Routes.INBOX -> counts.supportCount
                        else -> null
                    }
                    val showPulse = item.route == Routes.HOME && (counts.onlineCount ?: 0) > 0

                    DrawerNavRow(
                        icon       = item.icon,
                        label      = item.label,
                        active     = isActive,
                        badgeCount = badgeCount,
                        showPulse  = showPulse,
                        onClick    = {
                            val target = item.legacySidebarTarget ?: item.route
                            ctx.router.navigateTo(target)
                            onClose()
                        }
                    )
                    Box(Modifier.height(2.px))
                }
            }
        }

        // ── Pinned bottom ─────────────────────────────────────────────────────
        Column(
            Modifier
                .fillMaxWidth()
                .padding(leftRight = 10.px, topBottom = 10.px)
                .styleModifier { property("border-top", "1px solid rgba(255,255,255,.06)") }
        ) {
            // Settings
            DrawerNavRow(
                icon   = settingsNavItem.icon,
                label  = settingsNavItem.label,
                active = currentRoute == settingsNavItem.route,
                onClick = {
                    ctx.router.navigateTo(settingsNavItem.route)
                    onClose()
                }
            )
            Box(Modifier.height(4.px))

            // Logout
            val userSession = session
            Row(
                Modifier
                    .fillMaxWidth()
                    .sBorderRadius(10)
                    .padding(leftRight = 12.px, topBottom = 11.px)
                    .cursor(Cursor.Pointer)
                    .styleModifier {
                        property("transition", "background 140ms ease")
                    }
                    .classNames("na-drawer-logout-row")
                    .onClick {
                        userSession.signOut()
                        ctx.router.navigateTo(Routes.LOGIN)
                        onClose()
                    },
                verticalAlignment = Alignment.CenterVertically
            ) {
                LucideIcon(name = "log-out", size = 16, color = "rgba(240,244,252,.40)")
                Box(Modifier.width(12.px))
                SpanText(
                    "Выйти",
                    Modifier.styleModifier {
                        property("font-size", "14px")
                        property("font-weight", "500")
                        property("color", "rgba(240,244,252,.50)")
                        property("font-family", "'Inter', sans-serif")
                    }
                )
            }
        }
    }
}

// ── Drawer nav row ────────────────────────────────────────────────────────────

@Composable
private fun DrawerNavRow(
    icon: String,
    label: String,
    active: Boolean,
    badgeCount: Int? = null,
    showPulse: Boolean = false,
    onClick: () -> Unit,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .sBorderRadius(10)
            .padding(leftRight = 12.px, topBottom = 11.px)
            .cursor(Cursor.Pointer)
            .styleModifier {
                if (active) {
                    property("background", "rgba(0,97,255,.20)")
                } else {
                    property("background", "transparent")
                }
                property("transition", "background 140ms ease")
            }
            .classNames("na-drawer-nav-row")
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Icon with optional badge or pulse
        Box(
            Modifier
                .styleModifier { property("position", "relative") },
            contentAlignment = Alignment.Center
        ) {
            LucideIcon(
                name  = icon,
                size  = 17,
                color = if (active) "#8FB8FF" else "rgba(240,244,252,.55)"
            )
            // Badge dot
            if (badgeCount != null && badgeCount > 0) {
                Box(
                    Modifier
                        .width(7.px).height(7.px)
                        .borderRadius(50.percent)
                        .styleModifier {
                            property("background", "#FF6B5B")
                            property("position", "absolute")
                            property("top", "-2px")
                            property("right", "-2px")
                            property("box-shadow", "0 0 0 2px #070C16")
                        }
                )
            }
            // Online pulse dot
            if (showPulse) {
                Box(
                    Modifier
                        .width(6.px).height(6.px)
                        .borderRadius(50.percent)
                        .styleModifier {
                            property("background", "#00E5A4")
                            property("position", "absolute")
                            property("bottom", "-2px")
                            property("right", "-2px")
                            property("box-shadow", "0 0 0 2px #070C16, 0 0 6px #00E5A4")
                            property("animation", "na-pulse 2s ease-in-out infinite")
                        }
                )
            }
        }
        Box(Modifier.width(12.px))
        SpanText(
            label,
            Modifier.styleModifier {
                property("font-size", "14px")
                property("font-weight", if (active) "600" else "500")
                property("color", if (active) "#F0F4FC" else "rgba(240,244,252,.65)")
                property("font-family", "'Inter', sans-serif")
                property("flex", "1")
            }
        )
        // Badge count chip
        if (badgeCount != null && badgeCount > 0) {
            Box(
                Modifier
                    .sBorderRadius(999)
                    .padding(leftRight = 7.px, topBottom = 2.px)
                    .styleModifier { property("background", "#FF6B5B") },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    "$badgeCount",
                    Modifier.styleModifier {
                        property("font-size", "10px")
                        property("font-weight", "700")
                        property("color", "#FFFFFF")
                        property("font-family", "'Inter', sans-serif")
                    }
                )
            }
        }
    }
}

// ── Active route for drawer ───────────────────────────────────────────────────

private fun isDrawerActiveRoute(currentRoute: String, item: NavItem): Boolean {
    if (currentRoute == item.route) return true
    val legacy = item.legacySidebarTarget
    if (legacy != null && currentRoute == legacy) return true
    if (item.route != Routes.HOME && currentRoute.startsWith(item.route + "/")) return true
    return false
}

// ── Scroll lock helpers (plain JS to avoid Kobweb modifier scope conflicts) ───

private fun lockBodyScroll() {
    val body = document.body ?: return
    body.asDynamic().style.overflow = "hidden"
}

private fun unlockBodyScroll() {
    val body = document.body ?: return
    body.asDynamic().style.overflow = ""
}

// ── CSS for mobile nav drawer ─────────────────────────────────────────────────

@Composable
private fun MobileNavDrawerStyles() {
    val id = "na-mobile-drawer-styles"
    androidx.compose.runtime.LaunchedEffect(Unit) {
        val existing = document.getElementById(id)
        if (existing != null) return@LaunchedEffect
        val style = document.createElement("style")
        style.id = id
        style.textContent = """
            @keyframes na-drawer-left-out {
                from { transform: translateX(0); }
                to   { transform: translateX(-100%); }
            }
            @keyframes na-drawer-left-in {
                from { transform: translateX(-100%); }
                to   { transform: translateX(0); }
            }
            @keyframes na-scrim-in {
                from { opacity: 0; }
                to   { opacity: 1; }
            }
            .na-drawer-nav-row:hover { background: rgba(255,255,255,.04) !important; }
            .na-drawer-nav-row:active { transform: scale(0.98); }
            .na-drawer-logout-row:hover { background: rgba(255,255,255,.03) !important; }
            /* Hide scrollbar inside drawer */
            .na-mobile-drawer-scroll::-webkit-scrollbar { display: none; }
        """.trimIndent()
        document.head?.appendChild(style)
    }
}
